import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

interface PrintBarcodeModalData {
  shelf: any;
  productName: string;
  variantId: string;
}

interface BarcodeItem {
  serialNumber: string;
  shelfCode: string;
  barcode?: string;
  quantity?: number;
}

@Component({
  selector: 'mega-store-factory-print-barcode-modal',
  templateUrl: './print-barcode-modal.component.html',
  styleUrls: ['./print-barcode-modal.component.scss']
})
export class PrintBarcodeModalComponent {
  constructor(
    public dialogRef: MatDialogRef<PrintBarcodeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PrintBarcodeModalData
  ) {}

  getBarcodeItems(): BarcodeItem[] {
    // If we have the actual shelf data with barcode items (from user's provided structure)
    if (this.data.shelf.shelves && Array.isArray(this.data.shelf.shelves)) {
      return this.data.shelf.shelves.map((item: any) => ({
        serialNumber: item.serialNumber,
        shelfCode: item.shelfCode,
        barcode: item.barcode,
        quantity: item.quantity || 0
      }));
    }
    
    // If we have a single shelf item with serial number
    if (this.data.shelf.serialNumber) {
      return [{
        serialNumber: this.data.shelf.serialNumber,
        shelfCode: this.data.shelf.shelfCode,
        barcode: this.data.shelf.barcode,
        quantity: this.data.shelf.quantity || 0
      }];
    }
    
    // Default fallback - create a single item
    return [{
      serialNumber: `SN-${Date.now()}`,
      shelfCode: this.data.shelf.shelve || 'Unknown',
      barcode: undefined,
      quantity: this.data.shelf.quantity || this.data.shelf.allocatedQuantity || 0
    }];
  }

  private generateSerialNumber(index: number): string {
    // Generate a unique serial number based on shelf info and index
    const shelfCode = this.data.shelf.shelve || 'SH';
    const timestamp = Date.now();
    return `${shelfCode}-${timestamp}-${index + 1}`;
  }

  copyToClipboard(serialNumber: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      // Use the modern Clipboard API
      navigator.clipboard.writeText(serialNumber).then(
        () => {
          console.log('Serial number copied to clipboard successfully');
          // You could add a toast notification here if needed
        },
        (err) => {
          console.error('Failed to copy serial number: ', err);
          this.fallbackCopyTextToClipboard(serialNumber);
        }
      );
    } else {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(serialNumber);
    }
  }

  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Serial number copied to clipboard using fallback method');
      } else {
        console.error('Fallback: Oops, unable to copy');
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onPrint(item: BarcodeItem): void {
    this.dialogRef.close({
      print: true,
      item: item,
      variantId: this.data.variantId
    });
  }

  onPrintAll(): void {
    this.dialogRef.close({
      print: true,
      items: this.getBarcodeItems(),
      variantId: this.data.variantId
    });
  }
} 